import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import type { TaskInstance } from 'ember-concurrency';
import type { Hotel } from 'frontend-utils/models';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import type HotelPricingManagerController from 'lumen/controllers/hotel/pricing-manager';
import type {
  Carecommendation,
  Override,
  PaPmsInfo,
  PaRecommendationReason,
  PaSubscriptionMetaInfo,
  Period,
  RestrictionRecommendation,
} from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { PrimaSettingsInProgress } from 'lumen/services/pricing-assistant-data-request-service';
import type { Transition } from 'lumen/services/transition';
import type {
  MealTypeValues,
  MembershipType,
  RateParam,
} from 'lumen/utils/constants/general-constants';
import {
  type OpenRoomPricingFixedCategory,
  PRIMA_VIEWS,
} from 'lumen/utils/constants/pricing-assistant-constants';
import {
  getStayDateRangeRequestDates,
  isStayDateRangeEnabled,
} from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';
import { getCalendarMonthDateRanges } from 'lumen/utils/pricing-assistant/generate-calendar-daterange';
import { generateRecommendationsRequestDaterange } from 'lumen/utils/pricing-assistant/generate-recommendations-request-daterange';
import { validateOpenPricingCalendarParams } from 'lumen/utils/pricing-assistant/open-pricing-utils';

type Model = {
  overridesRequestTaskResult?: TaskInstance<Override[] | null>;
  pricingAssistantCompetitorsRatesRequestTaskResult?: TaskInstance<
    Period[] | null
  >;
  pricingAssistantGeneralDataRequestsTaskResult: TaskInstance<void>;
  pricingAssistantRatesRequestTaskResult: TaskInstance<Period[] | null>;
  pricingAssistantRecommendationsRequestTaskResult: TaskInstance<
    Carecommendation[] | null
  >;
  pricingAssistantroomOffsetHierarchyTaskResult?: TaskInstance<string[]>;
  primaSettingsInProgressTaskResult: TaskInstance<PrimaSettingsInProgress | null>;
  // optional: only if PMS is integrated
  requestPricingAssistantPmsInfoTaskResult?: TaskInstance<PaPmsInfo | null>;
  requestPricingAssistantRecommendationReasonsTaskResult: TaskInstance<
    PaRecommendationReason[] | null
  > | null;
  requestPricingAssistantSubscriptionMetaTaskResult: TaskInstance<PaSubscriptionMetaInfo | null>;
  restrictionsRecommendationsRequestTaskResult?: TaskInstance<
    RestrictionRecommendation[] | null
  >;
  restrictionsRequestTaskResult?: TaskInstance<Period[] | null>;
};

export type Params = {
  compsetId: string;
  fromDate: string;
  los: number;
  maxPersons: string;
  mealPlan: string;
  membershipType: MembershipType;
  month: string;
  otaId: string;
  platform: Platform;
  rateCode: string;
  rateParam: RateParam;
  refDaysAgo: number;
  roomType: string;
  toDate: string;
  view: string;
  week: string;
};

export default class HotelPricingManagerRoute extends Route {
  // `month` is authoritative for the calendar view. `fromDate`/`toDate` take
  // precedence over it only in table view for DRP hotels with the
  // PRICING_MANAGER_RMS_PIVOT_TABLE toggle on; calendar view never sees a range.
  queryParams = {
    compsetId: {
      refreshModel: false,
    },
    fromDate: {
      refreshModel: true,
    },
    los: {
      refreshModel: false,
    },
    maxPersons: {
      refreshModel: false,
    },
    mealPlan: {
      refreshModel: false,
    },
    membershipType: {
      refreshModel: false,
    },
    month: {
      refreshModel: true,
    },
    otaId: {
      refreshModel: false,
    },
    platform: {
      refreshModel: false,
    },
    rateParam: {
      refreshModel: false,
    },
    ratesCode: {
      refreshModel: false,
    },
    refDaysAgo: {
      refreshModel: false,
    },
    roomType: {
      refreshModel: false,
    },
    toDate: {
      refreshModel: true,
    },
    view: {
      refreshModel: false,
    },
    week: {
      refreshModel: false,
    },
  };

  @service declare access: Services['access'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare liveupdate: Services['liveupdate'];
  @service('marketing/recommendation')
  declare marketingRecommendationService: Services['marketing/recommendation'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service declare pricingAssistantPmsSs: Services['pricing-assistant-pms-ss'];
  @service
  declare pricingAssistantRecommendationActionPoller: Services['pricing-assistant-recommendation-action-poller'];
  @service('restrictions/action-poller')
  declare restrictionsActionPoller: Services['restrictions/action-poller'];

  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  get hotelId(): string {
    return this.hotelAndMarketService.hotel.id!;
  }

  // For the 2-way pilot we want to hide calendar/table view switcher for Multi-Room users. For more information check COA-3021
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isViewSwitcherEnabled() {
    return (
      !this.access.hasPricingAssistantOpenPricingFeature &&
      !this.access.hasPricingAssistantDynamicRatePricingFeature
    );
  }

  activate(transition: Transition): void {
    super.activate(transition);

    // Currently we only have liveshop for PA in RI version
    if (!this.access.isPricingAssistantStandaloneVersion) {
      this.liveupdate.pricingAssistantTabOpen = true;
    }
  }

  async beforeModel(transition: Transition): Promise<void> {
    if (!this.access.hasPricingAssistantTab) {
      if (this.access.hasRates) {
        transition.abort();
        this.router.transitionTo(
          'hotel.rates',
          this.hotelAndMarketService.hotel,
        );
      } else {
        this.transition.redirectToEnabledTab(transition);
      }
      return;
    } else {
      // Awaited rather than gated in the template: the QP validation below
      // defaults the pricing selection params off the preference and redirects.
      // After the access guard so redirected users don't wait for it.
      await this.pricingAssistantDataRequestService.loadPaRatePreferenceTask.perform(
        this.hotelId,
      );

      this.transition.validatePricingAssistantQueryParams(transition);
    }

    this.pricingAssistantRecommendationActionPoller.stopPolling();
    this.restrictionsActionPoller.stopPolling();

    // Cancel any running tracking task to Segment before unloading carate & carecommendation
    // -> since we rely on value of those models in that tracking task anyway
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.pricingAssistantDataRequestService.trackReturnedRecommendationsTask.cancelAll();

    this.store.unloadAll('carate');
    this.store.unloadAll('rate');
    this.store.unloadAll('carecommendation');
    this.store.unloadAll('override');
  }

  deactivate(): void {
    // Currently we only have liveshop for PA in RI version
    if (!this.access.isPricingAssistantStandaloneVersion) {
      this.liveupdate.pricingAssistantTabOpen = false;
    }

    // Cancel all pricing mode related tasks when leaving the route
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.pricingAssistantDataRequestService.pricingAssistantGeneralDataRequestsTask.cancelAll();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.pricingAssistantDataRequestService.pricingAssistantRecommendationsRequestTask.cancelAll();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.pricingAssistantDataRequestService.pricingAssistantRatesRequestTask.cancelAll();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.pricingAssistantDataRequestService.requestOverridesTask.cancelAll();

    // Cancel any running tracking task to Segment when leaving the route
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.pricingAssistantDataRequestService.trackReturnedRecommendationsTask.cancelAll();

    this.pricingAssistantRecommendationActionPoller.stopPolling();
    this.pricingAssistantRecommendationActionPoller.resetRecommendationsWithPushAnimation();
    this.restrictionsActionPoller.stopPolling();

    /**
     * In application tests, the app gets torn down after each test. This causes the store to be destroyed
     * before the deactivate hook is called. This check is to prevent the store from being accessed after it's destroyed.
     */
    if (this.store.isDestroyed) {
      return;
    }

    this.marketingRecommendationService.cleanUp();

    // this.store.unloadAll('day');
    this.store.unloadAll('carate');
    this.store.unloadAll('rate');
    this.store.unloadAll('carecommendation');
    // this.store.unloadAll('period');
    this.store.unloadAll('holiday');
    this.store.unloadAll('event');
    this.store.unloadAll('occupancy');
    this.store.unloadAll('predicted-occupancy');
    this.store.unloadAll('pa-recommendation-reason');
    this.store.unloadAll('override');
  }

  model(params: Params): Model {
    const { fromDate, month, toDate, view } = params;

    // dateRangeStart & dateRangeEnd contain the days shown in the calendar
    const { dateRangeStart, dateRangeEnd, compareDate, calendarMonth } =
      getCalendarMonthDateRanges(month, this.hotelAndMarketService.timezone);

    const isDateRangeActive = Boolean(
      isStayDateRangeEnabled(this.featureToggle, this.access, view) &&
      fromDate &&
      toDate,
    );
    const {
      dateRangeStart: effectiveDateRangeStart,
      dateRangeEnd: effectiveDateRangeEnd,
      dateRangeStartForRecommendationsRequest,
      dateRangeEndForRecommendationsRequest,
      occupancyMonth,
    } = isDateRangeActive
      ? {
          ...getStayDateRangeRequestDates(
            this.hotelAndMarketService.toLocalTime(fromDate),
            this.hotelAndMarketService.toLocalTime(toDate),
            this.hotelAndMarketService.getLocalTime(),
          ),
          occupancyMonth: this.hotelAndMarketService.toLocalTime(fromDate),
        }
      : {
          dateRangeStart,
          dateRangeEnd,
          ...generateRecommendationsRequestDaterange(
            this.hotelAndMarketService,
            calendarMonth,
          ),
          occupancyMonth: calendarMonth,
        };

    // Request events, holidays & occupancy data
    const generalDataRequestsTaskResult =
      this.pricingAssistantDataRequestService.pricingAssistantGeneralDataRequestsTask.perform(
        this.hotelId,
        effectiveDateRangeStart,
        effectiveDateRangeEnd,
        occupancyMonth,
      );

    // Initial perform so that we fetch the alerts on page load
    const ratesRequestTaskResult =
      this.pricingAssistantDataRequestService.pricingAssistantRatesRequestTask.perform(
        this.hotelId,
        effectiveDateRangeStart,
        effectiveDateRangeEnd,
        compareDate,
      );

    let competitorsRatesRequestTaskResult;
    let overridesRequestTaskResult;
    let restrictionsRequestTaskResult;
    let restrictionsRecommendationsRequestTaskResult;

    // TODO-COA-2719: The hasPricingAssistantOpenPricingFeature should be removed when adding the RI rates to the calendar. Keep in mind that hotel.roomTypeMapping only exists for ORP subs
    if (
      this.access.hasRates &&
      this.access.hasPricingAssistantOpenPricingFeature
    ) {
      competitorsRatesRequestTaskResult =
        this.pricingAssistantDataRequestService.pricingAssistantCompetitorRatesRequestTask.perform(
          this.hotelId,
          effectiveDateRangeStart,
          effectiveDateRangeEnd,
          compareDate,
          this.hotelAndMarketService.hotel.roomTypeMapping[
            this.hotelAndMarketService.hotel.caxxxPmsSettings
              .pms_leading_room_id
          ] as OpenRoomPricingFixedCategory,
          params,
        );
    }

    let roomOffsetHierarchyStrategyTaskResult;
    if (this.access.hasPricingAssistantDynamicRatePricingFeature) {
      roomOffsetHierarchyStrategyTaskResult =
        this.pricingAssistantDataRequestService.roomOffsetHierarchyTask.perform();

      overridesRequestTaskResult =
        this.pricingAssistantDataRequestService.requestOverridesTask.perform(
          this.hotelId,
          effectiveDateRangeStart,
          effectiveDateRangeEnd,
        );

      if (this.access.hasPricingAssistantOverbookingRestrictionsFeature) {
        restrictionsRequestTaskResult =
          this.pricingAssistantDataRequestService.restrictionsRequestTask.perform(
            this.hotelId,
            effectiveDateRangeStart,
            effectiveDateRangeEnd,
          );

        restrictionsRecommendationsRequestTaskResult =
          this.pricingAssistantDataRequestService.restrictionsRecommendationsRequestTask.perform(
            this.hotelId,
            effectiveDateRangeStart,
            effectiveDateRangeEnd,
          );

        void this.pricingAssistantDataRequestService.requestRestrictionActionsOnPageLoadTask.perform(
          this.hotelId,
          effectiveDateRangeStart,
          effectiveDateRangeEnd,
        );
      }
    }

    const recommendationsRequestTaskResult =
      this.pricingAssistantDataRequestService.pricingAssistantRecommendationsRequestTask.perform(
        this.hotelId,
        dateRangeStartForRecommendationsRequest,
        dateRangeEndForRecommendationsRequest,
        true, // restartRecommendationPollers
      );

    const subscriptionMetaTaskResult =
      this.pricingAssistantDataRequestService.requestPricingAssistantSubscriptionMetaTask.perform(
        this.hotelId,
      );

    // Only request PMS info if the PMS is integrated
    let requestPmsInfoTaskResult = null;
    if (this.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated) {
      requestPmsInfoTaskResult =
        this.pricingAssistantDataRequestService.requestPricingAssistantPmsInfoTask.perform(
          this.hotelId,
        );
    }

    // use same date range as recommendations request
    let recommendationReasonsTaskResult = null;

    // for open pricing subscriptions,
    // we fetch these reasons in the day detail route instead
    if (!this.access.hasPricingAssistantOpenPricingFeature) {
      // use same date range as recommendations request
      recommendationReasonsTaskResult =
        this.pricingAssistantDataRequestService.pricingAssistantRecommendationReasonsRequestTask.perform(
          this.hotelId,
          dateRangeStartForRecommendationsRequest,
          dateRangeEndForRecommendationsRequest,
        );
    }

    // Currently we only have liveshop for PA in RI version
    if (!this.access.isPricingAssistantStandaloneVersion) {
      // Make sure that the liveupdate service knows what is selected - we only pass params that are relevant to the pricing mode selections
      // This is important so we can show the spinner if a liveupdate is active for the selection
      // Note: Liveshop expect maxPersons and mealPlan to be integers. However, they're not automatically casted to numbers as in Rates, since they don't have a default value (set to null)
      // In Ember, QP are type casted to their default value type by default (https://guides.emberjs.com/release/routing/query-params/#toc_default-values-and-deserialization)
      // For table view we liveshop based on base room number of guests as competitors are only shown for base room.
      const maxPersons =
        view === PRIMA_VIEWS.TABLE
          ? this.hotelAndMarketService.hotel.paRatePreference.persons
          : parseInt(params.maxPersons, 10);
      this.liveupdate.updateSelection({
        currentHotelId: (this.modelFor('hotel') as Hotel).id,
        selectedOTA: params.otaId,
        los: params.los,
        month: params.month,
        maxPersons,
        rateParam: params.rateParam,
        mealtype: parseInt(params.mealPlan, 10) as MealTypeValues,
        roomtype: params.roomType,
        platform: params.platform,
        membershipType: params.membershipType,
        compsetId: parseInt(params.compsetId, 10),
      });
    }

    if (view === PRIMA_VIEWS.CALENDAR) {
      // NOTE: when the view switcher becomes visible and users can switch to
      // calendar view, fetchRecommendationsTask must also be triggered inside
      // switchTab() in the controller — otherwise recommendations won't load
      // on view switches, only on initial route load.
      void this.marketingRecommendationService.fetchRecommendationsTask.perform(
        this.hotelId,
        dateRangeStart,
        dateRangeEnd,
      );
    }

    // Config update status behind the nights and guests badges. Not awaited: the
    // badges sit in the filters row, which renders before the other requests
    // settle, so they appear when the value lands.
    const primaSettingsInProgressTaskResult =
      this.pricingAssistantDataRequestService.requestPrimaSettingsInProgressTask.perform(
        this.hotelId,
      );

    const model: Model = {
      pricingAssistantGeneralDataRequestsTaskResult:
        generalDataRequestsTaskResult,
      pricingAssistantRatesRequestTaskResult: ratesRequestTaskResult,
      pricingAssistantRecommendationsRequestTaskResult:
        recommendationsRequestTaskResult,
      pricingAssistantCompetitorsRatesRequestTaskResult:
        competitorsRatesRequestTaskResult,
      primaSettingsInProgressTaskResult,
      requestPricingAssistantSubscriptionMetaTaskResult:
        subscriptionMetaTaskResult,
      requestPricingAssistantRecommendationReasonsTaskResult:
        recommendationReasonsTaskResult,
    };
    if (requestPmsInfoTaskResult) {
      model['requestPricingAssistantPmsInfoTaskResult'] =
        requestPmsInfoTaskResult;
    }
    if (roomOffsetHierarchyStrategyTaskResult) {
      model['pricingAssistantroomOffsetHierarchyTaskResult'] =
        roomOffsetHierarchyStrategyTaskResult;
    }
    if (overridesRequestTaskResult) {
      model['overridesRequestTaskResult'] = overridesRequestTaskResult;
    }
    if (restrictionsRequestTaskResult) {
      model['restrictionsRequestTaskResult'] = restrictionsRequestTaskResult;
    }
    if (restrictionsRecommendationsRequestTaskResult) {
      model['restrictionsRecommendationsRequestTaskResult'] =
        restrictionsRecommendationsRequestTaskResult;
    }

    return model;
  }

  resetController(
    controller: HotelPricingManagerController,
    isExiting: boolean,
  ): void {
    if (isExiting) {
      controller.view = null;
    }
  }

  setupController(
    controller: HotelPricingManagerController,
    model: Model,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    // ORP dynamic filter values are fetched with rates request
    // Since these QPs are only used for rates filtering in the FE, we perform the validation here
    if (this.access.hasPricingAssistantOpenPricingFeature) {
      // We can't be sure if roomCategoryFiltersRequest is resolved by now
      // And we don't want to await it in the model hook as it makes the RI to PriMa transition slower
      if (model.pricingAssistantRatesRequestTaskResult.isRunning) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        model.pricingAssistantRatesRequestTaskResult.then(() => {
          validateOpenPricingCalendarParams(
            transition.to.queryParams,
            this.store,
            this.hotelAndMarketService,
            this.router,
          );
        });
      } else {
        validateOpenPricingCalendarParams(
          transition.to.queryParams,
          this.store,
          this.hotelAndMarketService,
          this.router,
        );
      }
    }
  }
}
